<template>
  <div class="flex flex-column gap-3">
    <h3>Payments</h3>

    <a-alert :type="paymentMessage.type" show-icon :message="paymentMessage.message"
      data-testid="shift-view-payment-status" />

    <payment-info v-if="paymentId" :payment-id.sync="paymentId" @change="loadData"
      :payments-list="listData.map(item => item.payment.id)" />

    <shift-section-state v-if="payments.length" :state="paymentDetailsState" empty-title="No payment details"
      empty-description="Payment request details for this shift will appear here."
      error-title="Payment details couldn't be loaded" error-description="Try loading the payment details again."
      test-id="shift-view-payment-details-state" @retry="loadData">
      <a-list item-layout="vertical" :data-source="listData" :style="{ overflow: 'auto', maxHeight: '600px' }">
        <a-list-item slot="renderItem" slot-scope="item" class="pr-2 flex flex-column gap-2">
          <div class="flex justify-content-between">
            <h4 class="flex gap-2">Payment Request #{{ item.payment.id }}</h4>
            <a-button icon="eye" @click="openPaymentModal(item.payment.id)" type="primary"> View Payment </a-button>
          </div>
          <a-descriptions :column="1" size="small" class="col-fixed p-0" :colon="false">
            <a-descriptions-item label="Payment Status">
              <a-tag class="m-0" :color="getPaymentStatus(item.payment.status).color">{{
                getPaymentStatus(item.payment.status).title
                }}</a-tag>
            </a-descriptions-item>

            <a-descriptions-item label="Shift Status">
              <a-tag v-if="getCurrentShiftStatus(item.shift_payments)" class="m-0"
                :color="getCurrentShiftStatus(item.shift_payments).color">{{
                  getCurrentShiftStatus(item.shift_payments).title }}</a-tag>
            </a-descriptions-item>

            <a-descriptions-item label="Reviewer" v-if="item.payment && item.payment.reviewer">
              <grouped-associate-info :first-name="item.payment.reviewer?.first_name"
                :last-name="item.payment.reviewer?.last_name" :photo-url="item.payment.reviewer?.photo_url"
                :associate-number="item.payment.reviewer?.associate_number" />
            </a-descriptions-item>

            <template v-if="item.payment.sender">
              <a-descriptions-item label="Invoice From">
                <grouped-associate-info v-if="item.payment.sender_type === 'associate'"
                  :first-name="item.payment.sender.first_name" :last-name="item.payment.sender.last_name"
                  :photo-url="item.payment.sender.photo_url" :associate-number="item.payment.sender.associate_number" />
                <div class="flex gap-2" v-else-if="item.payment.sender_type === 'project'">
                  <user-avatar :width="32" :height="32"
                    :image-path="item.payment.sender.logo ? item.payment.sender.logo : undefined" />
                  <div class="flex flex-column">
                    <b class="m-0 text-left">{{ item.payment.sender.name }}</b>
                    <p>
                      {{ collectAddress(item.payment.sender.address) }}
                    </p>
                  </div>
                </div>
                <div class="flex gap-2" v-else>
                  <user-avatar :width="32" :height="32" :image-path="undefined" />
                  <div class="flex flex-column">
                    <b class="m-0 text-left">{{ item.payment.sender.name }}</b>
                    <p>
                      {{ collectAddress(item.payment.sender.address) }}
                    </p>
                  </div>
                </div>
              </a-descriptions-item>
            </template>

            <a-descriptions-item v-if="item.payment.receiver" label="Bill To">
              <div class="flex gap-2">
                <user-avatar :width="32" :height="32"
                  :image-path="item.payment.receiver.logo ? item.payment.receiver.logo : undefined" />
                <div class="flex flex-column">
                  <b class="m-0 text-left">{{ item.payment.receiver.name }}</b>
                  <p>
                    {{ collectAddress(item.payment.receiver.address) }}
                  </p>
                </div>
              </div>
            </a-descriptions-item>
            <a-descriptions-item label="Author">
              <grouped-associate-info :first-name="item.associate.first_name" :last-name="item.associate.last_name"
                :photo-url="item.associate.photo_url" :associate-number="item.associate.associate_number" />
            </a-descriptions-item>
            <a-descriptions-item label="System">
              {{ combinedPaymentSystem(item.payment) }}
            </a-descriptions-item>
            <a-descriptions-item label="Shift Range">
              {{ getShiftRange(item.shift_payments) }}
            </a-descriptions-item>
            <a-descriptions-item label="Total, $"> {{ convertDbToMoney(item.payment.total) }}
            </a-descriptions-item><a-descriptions-item label="Subtotal, $">
              {{ convertDbToMoney(item.payment.net_amount) }}
            </a-descriptions-item>
            <a-descriptions-item label="Created At">
              {{ convertDate(item.payment.create_date) }}
            </a-descriptions-item>
            <a-descriptions-item label="Updated At">
              {{ convertDate(item.payment.update_date) }}
            </a-descriptions-item>
          </a-descriptions>
        </a-list-item>
      </a-list>
    </shift-section-state>
  </div>
</template>

<script>
import GroupedAssociateInfo from '@/components/ui/grouped-associate-info.vue'
import UserAvatar from '@/components/ui/user-avatar.vue'
import PaymentInfo from '@/modules/payment/components/info/payment-info.vue'
import util from '@/util'
import { Alert, Button, Tag, List, Modal, Descriptions } from 'ant-design-vue'
import moment from 'moment-timezone'
import { createNamespacedHelpers } from 'vuex'
import ShiftViewSectionState from '../shift-view-section-state.vue'

const { mapState: shiftState, mapGetters: shiftGetters } = createNamespacedHelpers('shifts')

const { mapActions: paymentActions, mapGetters: paymentGetters } = createNamespacedHelpers('payment')

export default {
  components: {
    'a-alert': Alert,
    'a-tag': Tag,
    'a-button': Button,
    'a-list': List,
    'a-list-item': List.Item,
    'a-descriptions': Descriptions,
    'a-descriptions-item': Descriptions.Item,
    'a-modal': Modal,
    'user-avatar': UserAvatar,
    'grouped-associate-info': GroupedAssociateInfo,
    'payment-info': PaymentInfo,
    'shift-section-state': ShiftViewSectionState
  },
  data() {
    return {
      paymentId: undefined,
      paymentDetailsState: 'idle',

      listData: [],
    }
  },
  watch: {
    payments: {
      handler(newVal) {
        if (newVal.length) {
          this.loadData()
        } else {
          this.listData = []
          this.paymentDetailsState = 'empty'
        }
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    ...shiftState({
      payments: state => state.shiftPayments,
      shift: state => state.shift.shift
    }),
    ...shiftGetters(['getShiftPaymentRequestStatus', 'getShiftPaymentStatus']),
    ...paymentGetters(['getPaymentStatus']),
    associateName() {
      const associate = this.shift.internal_payment_info.associate
      return `${associate.first_name} ${associate.last_name}`.trim()
    },
    draftCreatorName() {
      const creator = this.listData.find(item => item.payment?.status === 'draft')?.payment?.creator
      return creator ? `${creator.first_name} ${creator.last_name}`.trim() : this.associateName
    },
    paidDate() {
      const date = this.shift.internal_payment_info.fully_paid_at
      return date
        ? util.convertDateTimeToCurrentTimeZoneWithFormat(date, this.$store.state.applicationState.dateFormat)
        : null
    },
    paymentMessage() {
      const paymentInfo = this.shift.internal_payment_info

      if (paymentInfo.ready_for_pay_status === 'not_ready') {
        return {
          type: 'warning',
          message: "This shift isn't ready to pay. Resolve the open violations before payment can start."
        }
      }

      if (paymentInfo.ready_for_pay_status === 'blocked') {
        return {
          type: 'info',
          message:
            "This shift isn't paid. It records time that isn't directly worked — like training — so no payment will be issued for it."
        }
      }

      const messages = {
        draft: `${this.draftCreatorName} started a payment request for this shift but hasn't submitted it yet.`,
        not_paid: `This shift is ready to pay. ${this.associateName} hasn't requested payment for it yet.`,
        in_progress: 'Payment for this shift is being processed.',
        partially_paid: `${this.associateName} has been paid for part of this shift. The rest is still being processed.`,
        paid: `${this.associateName} was paid for this shift${this.paidDate ? ` on ${this.paidDate}` : ''}.`,
        cancelled: 'The payment request for this shift was rejected.'
      }

      return {
        type: paymentInfo.payment_status === 'cancelled' ? 'warning' : 'info',
        message:
          messages[paymentInfo.payment_status] ??
          `Payment status: ${this.getShiftPaymentRequestStatus(paymentInfo.payment_status)?.title ?? paymentInfo.payment_status ?? 'Unknown'}.`
      }
    }
  },
  methods: {
    ...paymentActions(['getPayment']),
    convertDbToMoney: util.convertDbToMoney,

    convertDate(date) {
      return util.convertDateTimeToCurrentTimeZoneWithFormat(date, this.$store.state.applicationState.dateFormat)
    },

    getCurrentShiftStatus(shift_payments) {
      const foundedShift = shift_payments.find(item => item.shift.id === this.shift.id)

      if (!foundedShift) return null

      return this.getShiftPaymentStatus(foundedShift.shift_payment.status)
    },

    combinedPaymentSystem(payment) {
      let method = '-'

      if (payment && payment.method) {
        method = util.capitalize(payment.method)
      }
      if (payment.method && payment.manual_payment_method) {
        method += ' ' + '(' + util.splitAndCapitalize(payment.manual_payment_method) + ')'
      }

      return method
    },

    getShiftRange(shift_payments) {
      const shift_range = shift_payments
        .map(item => item.shift.date)
        .sort((a, b) => moment(a).valueOf() - moment(b).valueOf())

      if (!shift_range.length) return '-'

      if (shift_range.length === 1) {
        return this.convertDate(shift_range[0])
      }

      return `${this.convertDate(shift_range[0])} - ${this.convertDate(
        shift_range[shift_range.length - 1]
      )} (${shift_range.length} days)`
    },

    collectAddress(address) {
      const parts = [
        address.address_line1,
        address.address_line2,
        address.city,
        address.country,
        address.state,
        address.zipcode
      ]

      const filteredParts = parts.filter(part => part)

      return filteredParts.join(', ')
    },

    openPaymentModal(id) {
      this.paymentId = id
    },

    async loadData() {
      this.paymentDetailsState = 'loading'

      try {
        const promises = this.payments.map(record => this.getPayment({ payment_id: record.payment_request_id }))

        this.listData = await Promise.all(promises)
        this.paymentDetailsState = this.listData.length ? 'ready' : 'empty'
      } catch (error) {
        this.paymentDetailsState = 'error'
      }
    }
  }
}
</script>

<style scoped>
::v-deep .ant-spin-nested-loading {
  min-height: 100px;
}

::v-deep .ant-list-split .ant-list-item {
  border: none !important;
}
</style>
